import React, { useState, useEffect } from 'react';
import { useFormik } from "formik";
import * as Yup from "yup";
import { IoClose } from 'react-icons/io5';
import { MdFastfood } from "react-icons/md";
import { CImage } from '@coreui/react';
import ImageUploading from 'react-images-uploading';
import AsyncSelect from 'react-select/async';
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

import { useTranslation } from 'react-i18next';
import { getCategorias, registerItem } from "../../services/api";
import { getStoredUser } from '../../utils/safeStorage';
import InputMoney from "../../pages/ListItem/InputMoney";
import '../../styles/modal-shared.css';

/**
 * ModalAddItem - Modal para cadastrar novos itens/tamanhos no cardapio
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Controla se o modal esta aberto
 * @param {function} props.onClose - Funcao chamada ao fechar o modal
 * @param {function} props.onSuccess - Funcao chamada apos salvar com sucesso
 * @param {Object} props.infoCategoria - Categoria pre-selecionada { value, label }
 * @param {number} props.modelo - Modelo do item (1 = simples, 2 = pizza)
 */
const ModalAddItem = ({
  isOpen,
  onClose,
  onSuccess,
  infoCategoria,
  modelo
}) => {
  const { t } = useTranslation('categorias');
  const user = getStoredUser();
  const userID = user?._id;

  const empresa = localStorage.getItem('empresa');
  const empresaParse = empresa ? JSON.parse(empresa) : {};
  const idEmpresa = empresaParse.id_empresa;

  const [valuePreco, setValuePreco] = useState(0);
  const [valueDescricao, setValueDescricao] = useState('');
  const [images, setImages] = useState([]);
  const [imageError, setImageError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const INITIAL_DATA = {
    value: "",
    label: t('addItemModal.categoryPlaceholder'),
  };
  const [selectData, setselectData] = useState(INITIAL_DATA);

  const maxNumber = 1;
  const maxMbFileSize = 2 * 1024 * 1024; // 2Mb

  // Resetar form quando modal abre com nova categoria
  useEffect(() => {
    if (isOpen) {
      if (infoCategoria) {
        setselectData(infoCategoria);
      } else {
        setselectData(INITIAL_DATA);
      }
      setValuePreco(0);
      setValueDescricao('');
      setImages([]);
      setImageError('');
      setIsSubmitting(false);
      formik.resetForm();
    }
  }, [isOpen, infoCategoria]);

  const mapResponseToValuesAndLabels = (data) => ({
    value: data._id,
    label: data.title,
  });

  async function callApi(value) {
    const data = await getCategorias()
      .then((response) => response.data.categorias)
      .then((response) => {
        if ((modelo?.value === 2 || modelo === 2)) {
          return response.filter((categoria) => (categoria.modelo?.value === 2 || categoria.modelo === 2));
        } else if ((modelo?.value === 1 || modelo === 1) || !modelo) {
          return response.filter((categoria) => (categoria.modelo?.value === 1 || categoria.modelo === 1) || !categoria.modelo);
        }
        return response;
      })
      .then((response) => response.sort((a, b) => a.order - b.order))
      .then((response) => response.map(mapResponseToValuesAndLabels))
      .then((response) => response.filter((i) => i.label.toLowerCase().includes(value?.toLowerCase() || '')));

    return data;
  }

  const onChangeImg = (imageList, addUpdateIndex) => {
    if (imageList && imageList.length > 0) {
      const image = imageList[0];

      if (!image.data_url || image.data_url === '' || !image.data_url.startsWith('data:image')) {
        toast.error(t('addItemModal.imageCloudError'), { autoClose: 6000 });
        setImageError(t('addItemModal.imageNotAvailable'));
        setImages([]);
        return;
      }

      if (image.data_url.length < 100) {
        toast.error(t('addItemModal.imageInvalidError'), { autoClose: 5000 });
        setImageError(t('addItemModal.imageCorrupted'));
        setImages([]);
        return;
      }
    }

    setImageError('');
    setImages(imageList);
  };

  const SignUpSchema = Yup.object().shape({
    title: Yup.string().required(t('addItemModal.required')),
    vinculo_categoria: !selectData.value && Yup.string().required(t('addItemModal.required')),
  });

  const formik = useFormik({
    validationSchema: SignUpSchema,
    validateOnBlur: false,
    validateOnChange: false,
    enableReinitialize: true,
    initialValues: {
      vinculo_categoria: "",
      title: "",
      preco: 0,
    },
    onSubmit: (values) => {
      const description = valueDescricao;
      const title = values.title;
      const price = values.preco;
      handleSubmitAPI(title, description, price);
    },
  });

  const handleSubmitAPI = (title, description, price) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const createdBy = userID;
    const out = false;
    const category_item_id = selectData.value;
    const category_item_title = selectData.label;
    const disponibilidade = 'sempre';
    const type = isPizza ? "pizza" : "simples";

    registerItem(createdBy, idEmpresa, category_item_id, category_item_title, title, description, out, images, price, disponibilidade, type)
      .then(cast => {
        if (cast.status === 201) {
          toast(cast.data.msg, { autoClose: 3000, type: "success" });
          if (onSuccess) {
            onSuccess();
          }
          onClose();
        } else {
          toast(cast.data.msg, { autoClose: 3000, type: "error" });
        }
      })
      .catch(err => {
        const msg = err.response?.data?.msg || t('addItemModal.saveError');
        toast(msg, { autoClose: 5000, type: "error" });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleMaxCharDescricao = (e) => {
    const newValue = e.target.value;
    if (newValue.length <= 1000) {
      setValueDescricao(newValue);
    }
  };

  const onChange = (event, nameObject, isNumber) => {
    setValuePreco(isNumber ? Number(event.target.value) : event.target.value);
    formik.setFieldValue("preco", isNumber ? Number(event.target.value) : event.target.value);
  };

  const isPizza = modelo?.value === 2 || modelo === 2;

  // Estilos do AsyncSelect alinhados ao design system
  const asyncSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '40px',
      border: state.isFocused ? '1.5px solid #318CD5' : '1.5px solid #e2e8f0',
      borderRadius: '10px',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(49, 140, 213, 0.12)' : 'none',
      fontSize: '14px',
      '&:hover': { borderColor: state.isFocused ? '#318CD5' : '#cbd5e1' }
    }),
    input: (base) => ({ ...base, color: '#1e293b', fontSize: '14px' }),
    singleValue: (base) => ({ ...base, color: '#1e293b', fontSize: '14px' }),
    placeholder: (base) => ({ ...base, color: '#94a3b8', fontSize: '14px' }),
    option: (base, { isFocused, isSelected }) => ({
      ...base,
      backgroundColor: isSelected ? '#318CD5' : isFocused ? '#eff6ff' : '#fff',
      color: isSelected ? '#fff' : '#1e293b',
      fontSize: '14px',
      cursor: 'pointer',
    }),
    menu: (base) => ({ ...base, borderRadius: '10px', overflow: 'hidden', zIndex: 10 }),
    menuList: (base) => ({ ...base, padding: '4px 0' }),
  };

  return (
    <div className={isOpen ? 'pdj-modal-overlay' : 'pdj-modal-overlay--hidden'}>
      <div className="pdj-modal-box">
        <form onSubmit={formik.handleSubmit}>

          {/* Header */}
          <div className="pdj-modal-header">
            <div className="pdj-modal-header-text">
              <h2 className="pdj-modal-title">
                {isPizza ? t('addItemModal.titlePizza') : t('addItemModal.title')}
              </h2>
              <p className="pdj-modal-subtitle">
                {isPizza ? t('addItemModal.subtitlePizza') : t('addItemModal.subtitle')}
              </p>
            </div>
            <button type="button" className="pdj-modal-close-btn" onClick={onClose}>
              <IoClose />
            </button>
          </div>

          {/* Body */}
          <div className="pdj-modal-body">

            {/* Categoria */}
            <div className="pdj-modal-field">
              <label className="pdj-modal-label">
                {t('addItemModal.category')}
                <span className="pdj-modal-required">*</span>
              </label>
              <AsyncSelect
                key={`cat-select-${modelo?.value || modelo || 'default'}`}
                loadOptions={callApi}
                onChange={(data) => setselectData(data)}
                value={selectData}
                defaultOptions
                id="vinculo_categoria"
                name="vinculo_categoria"
                placeholder={t('addItemModal.categoryPlaceholder')}
                styles={asyncSelectStyles}
              />
              {formik.errors.vinculo_categoria && (
                <span className="pdj-modal-error">{formik.errors.vinculo_categoria}</span>
              )}
            </div>

            {/* Nome do Item */}
            <div className="pdj-modal-field">
              <label className="pdj-modal-label">
                {t('addItemModal.itemName')}
                <span className="pdj-modal-required">*</span>
              </label>
              <input
                className="pdj-modal-input"
                placeholder={isPizza ? t('addItemModal.itemNamePlaceholderPizza') : t('addItemModal.itemNamePlaceholder')}
                id="title"
                type="text"
                name="title"
                onChange={formik.handleChange}
                value={formik.values.title}
              />
              {formik.errors.title && (
                <span className="pdj-modal-error">{formik.errors.title}</span>
              )}
            </div>

            {/* Descricao */}
            <div className="pdj-modal-field">
              <label className="pdj-modal-label">
                {t('addItemModal.description')}
              </label>
              <textarea
                className="pdj-modal-textarea"
                placeholder={t('addItemModal.descriptionPlaceholder')}
                value={valueDescricao}
                onChange={handleMaxCharDescricao}
                maxLength="1000"
              />
              <span className="pdj-modal-char-count">
                {valueDescricao.length}/1000
              </span>
            </div>

            {/* Preco (apenas se nao for pizza) */}
            {!isPizza && (
              <div className="pdj-modal-field" style={{ maxWidth: '240px' }}>
                <label className="pdj-modal-label">
                  {t('addItemModal.price')}
                </label>
                <InputMoney
                  onChange={(event) => onChange(event, 'price', true)}
                  value={valuePreco}
                  className="pdj-modal-input"
                  placeholder="0,00"
                />
              </div>
            )}

            {/* Imagem */}
            <div className="pdj-modal-field">
              <label className="pdj-modal-label">
                {t('addItemModal.image')}
              </label>
              <ImageUploading
                multiple
                value={images}
                onChange={onChangeImg}
                maxNumber={maxNumber}
                maxFileSize={maxMbFileSize}
                acceptType={["jpg", "jpeg", "png", "webp"]}
                dataURLKey="data_url"
              >
                {({
                  imageList,
                  onImageUpload,
                  onImageUpdate,
                  onImageRemove,
                  isDragging,
                  dragProps,
                  errors
                }) => (
                  <div>
                    <div
                      onClick={images.length === 0 ? onImageUpload : undefined}
                      {...dragProps}
                      className={`pdj-modal-upload${isDragging ? ' pdj-modal-upload--dragging' : ''}${imageList.length > 0 ? ' pdj-modal-upload--has-image' : ''}`}
                    >
                      {imageList.length === 0 && (
                        <>
                          <MdFastfood className="pdj-modal-upload-icon" />
                          <div className="pdj-modal-upload-title">
                            {t('addItemModal.imageChoose')}
                          </div>
                          <div className="pdj-modal-upload-hint">
                            {isDragging ? "Solte aqui..." : t('addItemModal.imageDragDrop')}
                          </div>
                        </>
                      )}
                      {imageList.map((image, index) => (
                        <div key={index} className="pdj-modal-upload-preview">
                          <CImage
                            rounded
                            thumbnail
                            fluid
                            src={image['data_url']}
                            style={{ maxHeight: '140px', maxWidth: '100%', borderRadius: '8px' }}
                          />
                          <div className="pdj-modal-upload-actions">
                            <button
                              type="button"
                              className="pdj-modal-btn pdj-modal-btn--secondary"
                              onClick={(e) => { e.stopPropagation(); onImageUpdate(index); }}
                            >
                              {t('addItemModal.imageChange')}
                            </button>
                            <button
                              type="button"
                              className="pdj-modal-btn pdj-modal-btn--danger"
                              onClick={(e) => { e.stopPropagation(); onImageRemove(index); }}
                            >
                              {t('addItemModal.imageRemove')}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {errors && (
                      <div style={{ marginTop: '4px' }}>
                        {errors.maxNumber && <span className="pdj-modal-error">{t('addItemModal.imageMaxNumber')}</span>}
                        {errors.acceptType && <span className="pdj-modal-error">{t('addItemModal.imageInvalidFormat')}</span>}
                        {errors.maxFileSize && <span className="pdj-modal-error">{t('addItemModal.imageMaxFileSize')}</span>}
                      </div>
                    )}

                    {imageError && (
                      <div style={{ marginTop: '4px' }}>
                        <span className="pdj-modal-error">{imageError}</span>
                      </div>
                    )}

                    <div className="pdj-modal-upload-info">
                      {t('addItemModal.imageMaxSize')}
                    </div>
                  </div>
                )}
              </ImageUploading>
            </div>

          </div>

          {/* Footer */}
          <div className="pdj-modal-footer">
            <button
              type="button"
              className="pdj-modal-btn pdj-modal-btn--secondary"
              onClick={onClose}
            >
              {t('actions.cancel')}
            </button>
            <button
              type="submit"
              className="pdj-modal-btn pdj-modal-btn--primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('addItemModal.saving') : t('actions.save')}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default ModalAddItem;
